import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface TournamentSummary {
    torneoClave: number;
    torneoNombre: string;
    fechaInicio: string;
    fechaTermino: string;
    bowlerCount: number;
    unpaidCount: number;
}

interface Bowler {
    clave: number;
    fichanal: number;
    nombreCompleto: string;
    email: string;
    webfee: number | null;
}

interface ClaimBowler {
    clave: number;
    fichanal: number;
    nombreCompleto: string;
    email: string;
}

const WEBFEE_AMOUNT = 10;
const PAGE_SIZE = 20;

const isFeeApplicable = (fechaInicio: string | null) => {
    if (!fechaInicio) return false;
    try {
        const d = new Date(fechaInicio);
        return d.getFullYear() > 2026 || (d.getFullYear() === 2026 && d.getMonth() >= 5);
    } catch { return false; }
};

const formatDate = (d: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d ?? ''; }
};

export default function RecruiterRoster() {
    const navigate = useNavigate();

    function YoutubeLite({ id, title }: { id: string; title: string }) {
        const [active, setActive] = useState(false);
        return (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-700">{title}</p>
                </div>
                <div
                    className="relative w-full cursor-pointer"
                    style={{ paddingBottom: '56.25%' }}
                    onClick={() => setActive(true)}
                >
                    {active ? (
                        <iframe
                            className="absolute inset-0 w-full h-full"
                            src={`https://www.youtube.com/embed/${id}?autoplay=1`}
                            title={title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    ) : (
                        <>
                            <img
                                className="absolute inset-0 w-full h-full object-cover"
                                src={`https://img.youtube.com/vi/${id}/maxresdefault.jpg`}
                                alt={title}
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="bg-black/60 hover:bg-red-600 rounded-full w-14 h-14 flex items-center justify-center transition shadow-lg">
                                    <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z" />
                                    </svg>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    const [recruiterClave, setRecruiterClave] = useState<number | null>(null);
    const [recruiterName, setRecruiterName] = useState('');
    const [recruiterEmail, setRecruiterEmail] = useState('');
    const [tournaments, setTournaments] = useState<TournamentSummary[]>([]);

    // ── Claim modal state ─────────────────────────────────────────────
    const [claimModalOpen, setClaimModalOpen] = useState(false);
    const [claimSearch, setClaimSearch] = useState('');
    const [claimResults, setClaimResults] = useState<ClaimBowler[]>([]);
    const [claimSearchLoading, setClaimSearchLoading] = useState(false);
    const [claimSearchDone, setClaimSearchDone] = useState(false);
    const [claimCart, setClaimCart] = useState<ClaimBowler[]>([]);
    const [claimSubmitting, setClaimSubmitting] = useState(false);
    const [claimSuccess, setClaimSuccess] = useState(false);
    const [claimError, setClaimError] = useState('');
    const claimSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [loadingTournaments, setLoadingTournaments] = useState(true);
    const [errorTournaments, setErrorTournaments] = useState<string | null>(null);

    const [selectedTournament, setSelectedTournament] = useState<TournamentSummary | null>(null);
    const [bowlers, setBowlers] = useState<Bowler[]>([]);
    const [loadingBowlers, setLoadingBowlers] = useState(false);
    const [errorBowlers, setErrorBowlers] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [balances, setBalances] = useState<Record<number, number | null>>({});
    const [loadingBalances, setLoadingBalances] = useState(false);

    useEffect(() => {
        try {
            const raw = localStorage.getItem('loggedInPlayer');
            const player = raw ? JSON.parse(raw) : null;
            if (player?.CLAVE) {
                setRecruiterClave(player.CLAVE);
                setRecruiterName(player.NOMBRECOMPLETO || player.nombre || '');
                setRecruiterEmail(player.EMAIL || player.email || '');
            } else {
                navigate('/dashboard');
            }
        } catch {
            navigate('/dashboard');
        }
    }, [navigate]);

    useEffect(() => {
        if (!recruiterClave) return;
        setLoadingTournaments(true);
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recruiter/roster/${recruiterClave}`)
            .then(r => r.json())
            .then((data: TournamentSummary[]) => { setTournaments(data); setLoadingTournaments(false); })
            .catch(() => { setErrorTournaments('Failed to load roster.'); setLoadingTournaments(false); });
    }, [recruiterClave]);

    const openTournament = (t: TournamentSummary) => {
        setSelectedTournament(t);
        setSearch('');
        setPage(1);
        setBowlers([]);
        setBalances({});
        setErrorBowlers(null);
        setLoadingBowlers(true);
        setLoadingBalances(false);
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/recruiter/roster/${recruiterClave}/${t.torneoClave}`)
            .then(r => r.json())
            .then(async (data: Bowler[]) => {
                setBowlers(data);
                setLoadingBowlers(false);
                // Fetch all balances in parallel
                setLoadingBalances(true);
                const entries = await Promise.all(
                    data.map(b =>
                        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finances/balance/${t.torneoClave}/${b.clave}`)
                            .then(r => r.json())
                            .then((bal: { BALANCE: number }) => [b.clave, bal.BALANCE] as [number, number])
                            .catch(() => [b.clave, null] as [number, null])
                    )
                );
                setBalances(Object.fromEntries(entries));
                setLoadingBalances(false);
            })
            .catch(() => { setErrorBowlers('Failed to load bowlers.'); setLoadingBowlers(false); });
    };

    const totalOwed = tournaments.reduce((s, t) => s + (isFeeApplicable(t.fechaInicio) ? t.unpaidCount * WEBFEE_AMOUNT : 0), 0);
    const totalBowlers = tournaments.reduce((s, t) => s + t.bowlerCount, 0);

    const filteredBowlers = bowlers.filter(b =>
        !search ||
        b.nombreCompleto.toLowerCase().includes(search.toLowerCase()) ||
        String(b.fichanal).includes(search)
    );

    const totalPages = Math.ceil(filteredBowlers.length / PAGE_SIZE);
    const pagedBowlers = filteredBowlers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleSearchChange = (val: string) => {
        setSearch(val);
        setPage(1);
    };

    const handlePrint = (tournament: TournamentSummary, currentBalances: Record<number, number | null>) => {
        const paid   = bowlers.filter(b => b.webfee === 1).length;
        const unpaid = bowlers.filter(b => b.webfee !== 1).length;
        const printDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        const tournDate = formatDate(tournament.fechaInicio);
        const showFeeCol = isFeeApplicable(tournament.fechaInicio);

        const fmtBalance = (bal: number | null | undefined) => {
            if (bal === null || bal === undefined) return '<span style="color:#94a3b8;">N/A</span>';
            if (bal === 0) return '<span style="color:#15803d;font-weight:700;">✓ Paid</span>';
            if (bal < 0) return `<span style="color:#b45309;font-weight:700;">Owes $${Math.abs(bal).toFixed(2)}</span>`;
            return `<span style="color:#1d4ed8;font-weight:700;">Credit $${bal.toFixed(2)}</span>`;
        };

        const rows = bowlers.map((b, i) => `
            <tr style="background:${i % 2 === 0 ? '#ffffff' : '#f8fafc'};">
                <td style="padding:9px 14px;color:#94a3b8;font-weight:600;font-size:12px;">${i + 1}</td>
                <td style="padding:9px 14px;font-weight:600;color:#0f172a;font-size:13px;">${b.nombreCompleto}</td>
                <td style="padding:9px 14px;text-align:center;color:#475569;font-size:13px;">${b.fichanal}</td>
                ${showFeeCol ? `<td style="padding:9px 14px;text-align:center;">
                    ${b.webfee === 1
                        ? `<span style="background:#dcfce7;color:#15803d;border-radius:20px;padding:3px 12px;font-weight:700;font-size:11px;letter-spacing:.3px;">✓ PAID</span>`
                        : `<span style="background:#fef3c7;color:#b45309;border-radius:20px;padding:3px 12px;font-weight:700;font-size:11px;letter-spacing:.3px;">$10 OWED</span>`
                    }
                </td>` : ''}
                <td style="padding:9px 14px;text-align:center;font-size:13px;">${fmtBalance(currentBalances[b.clave])}</td>
            </tr>`).join('');

        const statsHtml = showFeeCol ? `
        <div class="stat-card stat-total">
            <div class="num">${bowlers.length}</div>
            <div class="lbl">Total</div>
        </div>
        <div class="stat-card stat-paid">
            <div class="num">${paid}</div>
            <div class="lbl">Fee Paid</div>
        </div>
        <div class="stat-card ${unpaid > 0 ? 'stat-owed' : 'stat-clear'}">
            <div class="num">$${unpaid * WEBFEE_AMOUNT}</div>
            <div class="lbl">${unpaid > 0 ? `${unpaid} Unpaid` : 'All Paid'}</div>
        </div>` : `
        <div class="stat-card stat-total">
            <div class="num">${bowlers.length}</div>
            <div class="lbl">Total Bowlers</div>
        </div>`;

        const feeColHeader = showFeeCol ? '<th>Fee Status</th>' : '';
        const feeThStyle   = showFeeCol ? 'thead th:nth-child(4) { text-align: center; width: 110px; } thead th:last-child { text-align: center; width: 120px; }' : 'thead th:last-child { text-align: center; width: 120px; }';

        const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8" />
    <title>Roster – ${tournament.torneoNombre}</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; background: #fff; color: #0f172a; }
        .page { max-width: 780px; margin: 0 auto; padding: 36px 40px 48px; }
        /* Header */
        .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 18px; border-bottom: 3px solid #4f46e5; margin-bottom: 24px; }
        .header-left .org { font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #6366f1; margin-bottom: 6px; }
        .header-left .title { font-size: 22px; font-weight: 800; color: #0f172a; line-height: 1.2; }
        .header-left .date  { font-size: 13px; color: #64748b; margin-top: 4px; }
        .header-left .recruiter { font-size: 13px; color: #4f46e5; font-weight: 600; margin-top: 6px; }
        .header-right { text-align: right; font-size: 11px; color: #94a3b8; line-height: 1.8; }
        .header-right .brand { font-size: 18px; font-weight: 800; color: #4f46e5; margin-bottom: 2px; }
        /* Stats */
        .stats { display: flex; gap: 16px; margin-bottom: 28px; }
        .stat-card { flex: 1; border-radius: 10px; padding: 14px 18px; text-align: center; }
        .stat-card .num { font-size: 28px; font-weight: 800; }
        .stat-card .lbl { font-size: 11px; font-weight: 600; letter-spacing: .5px; margin-top: 2px; }
        .stat-total  { background: #f1f5f9; border: 1px solid #e2e8f0; }
        .stat-total .num { color: #0f172a; } .stat-total .lbl { color: #64748b; }
        .stat-paid   { background: #f0fdf4; border: 1px solid #bbf7d0; }
        .stat-paid .num  { color: #15803d; } .stat-paid .lbl  { color: #15803d; }
        .stat-owed   { background: #fffbeb; border: 1px solid #fde68a; }
        .stat-owed .num  { color: #b45309; } .stat-owed .lbl  { color: #b45309; }
        .stat-clear  { background: #f0fdf4; border: 1px solid #bbf7d0; }
        .stat-clear .num { color: #15803d; } .stat-clear .lbl { color: #15803d; }
        /* Table */
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        thead tr { background: #1e1b4b; }
        thead th { padding: 10px 14px; color: #e0e7ff; font-weight: 700; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; }
        thead th:first-child { text-align: left; width: 40px; }
        thead th:nth-child(2) { text-align: left; }
        thead th:nth-child(3) { text-align: center; width: 90px; }
        ${feeThStyle}
        tbody tr td:first-child { color: #94a3b8; font-weight: 600; font-size: 12px; padding: 9px 14px; }
        tbody tr td { border-bottom: 1px solid #f1f5f9; }
        /* Footer */
        .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8; }
        @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .page { padding: 20px 28px; }
        }
    </style>
</head>
<body>
<div class="page">
    <div class="header">
        <div class="header-left">
            
            <div class="title">${tournament.torneoNombre}</div>
            <div class="date">${tournDate}</div>
            ${recruiterName ? `<div class="recruiter">Recruiter: ${recruiterName}</div>` : ''}
        </div>
        <div class="header-right">
            <div class="brand">TAT Bowling</div>
            <div>Printed ${printDate}</div>
            <div>${bowlers.length} Bowler${bowlers.length !== 1 ? 's' : ''}</div>
        </div>
    </div>

    <div class="stats">
        ${statsHtml}
    </div>

    <table>
        <thead>
            <tr>
                <th>#</th>
                <th>Bowler Name</th>
                <th>TAT #</th>
                ${feeColHeader}
                <th>Tournament Balance</th>
            </tr>
        </thead>
        <tbody>${rows}</tbody>
    </table>

    <div class="footer">
        <span>TAT Bowling — Recruiter Roster Report</span>
        <span>Generated ${printDate}</span>
    </div>
</div>
</body>
</html>`;

        const win = window.open('', '_blank', 'width=900,height=750');
        if (!win) { alert('Please allow popups to print.'); return; }
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => win.print(), 400);
    };

    // ── Claim modal handlers ──────────────────────────────────────────
    const handleCloseClaimModal = () => {
        if (claimSubmitting) return;
        setClaimModalOpen(false);
        setClaimSuccess(false);
        setClaimCart([]);
        setClaimSearch('');
        setClaimResults([]);
        setClaimSearchDone(false);
        setClaimError('');
    };

    const handleClaimSearch = (val: string) => {
        setClaimSearch(val);
        setClaimSearchDone(false);
        if (claimSearchTimer.current) clearTimeout(claimSearchTimer.current);
        if (!val.trim() || val.trim().length < 2) {
            setClaimResults([]);
            return;
        }
        claimSearchTimer.current = setTimeout(async () => {
            if (!selectedTournament) return;
            setClaimSearchLoading(true);
            try {
                const r = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/recruiter/tournament-bowler-search?q=${encodeURIComponent(val.trim())}&torneoClave=${selectedTournament.torneoClave}`
                );
                const data = await r.json();
                setClaimResults(Array.isArray(data) ? data : []);
            } catch {
                setClaimResults([]);
            } finally {
                setClaimSearchLoading(false);
                setClaimSearchDone(true);
            }
        }, 350);
    };

    const handleClaim = (b: ClaimBowler) => {
        if (claimCart.some(c => c.clave === b.clave)) return;
        setClaimCart(prev => [...prev, b]);
    };

    const handleUnclaim = (clave: number) => {
        setClaimCart(prev => prev.filter(c => c.clave !== clave));
    };

    const handleClaimSubmit = async () => {
        if (!claimCart.length || !recruiterClave || !selectedTournament) return;
        setClaimSubmitting(true);
        setClaimError('');

        const bowlerLines = claimCart
            .map((b, i) => `${i + 1}. ${b.nombreCompleto} | TAT# ${b.fichanal} | ${b.email}`)
            .join('\n');

        const questions = [
            'RECRUITER CLAIM REQUEST',
            `Tournament: ${selectedTournament.torneoNombre} (${formatDate(selectedTournament.fechaInicio)})`,
            '',
            `Recruiter Name: ${recruiterName}`,
            `Recruiter ID: ${recruiterClave}`,
            `Recruiter Email: ${recruiterEmail}`,
            '',
            `Bowlers Being Claimed (${claimCart.length}):`,
            bowlerLines,
        ].join('\n');

        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/ticket`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    bowlerClave: recruiterClave,
                    bowlerEmail: recruiterEmail,
                    reason: 'Recruiter Roster Claim Request',
                    category: 'Missing Bowler Claim',
                    subcategory: selectedTournament.torneoNombre,
                    questions,
                }),
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setClaimSuccess(true);
            } else {
                setClaimError(data.error || 'Failed to submit request. Please try again.');
            }
        } catch {
            setClaimError('An error occurred. Please try again.');
        } finally {
            setClaimSubmitting(false);
        }
    };

    // Auto-close claim modal 3 s after successful submission
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!claimSuccess) return;
        const t = setTimeout(() => {
            setClaimModalOpen(false);
            setClaimSuccess(false);
            setClaimCart([]);
            setClaimSearch('');
            setClaimResults([]);
            setClaimSearchDone(false);
        }, 3000);
        return () => clearTimeout(t);
    }, [claimSuccess]);

    // ── Bowler drill-down view ────────────────────────────────────────
    if (selectedTournament) {
        const unpaid = bowlers.filter(b => b.webfee !== 1).length;
        const paid   = bowlers.filter(b => b.webfee === 1).length;
        const showFee = isFeeApplicable(selectedTournament.fechaInicio);

        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-100">
                {/* Header */}
                <div className="bg-white shadow-md">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
                        <button
                            onClick={() => setSelectedTournament(null)}
                            className="text-gray-500 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100 transition"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <img src="/logo.avif" alt="TAT" className="h-10 w-auto" />
                        <div className="flex-1 min-w-0">
                            <h1 className="text-lg font-bold text-gray-900 leading-tight truncate">{selectedTournament.torneoNombre}</h1>
                            <p className="text-xs text-gray-500">{formatDate(selectedTournament.fechaInicio)}</p>
                        </div>
                        {!loadingBowlers && bowlers.length > 0 && (
                            <button
                                onClick={() => handlePrint(selectedTournament, balances)}
                                disabled={loadingBalances}
                                title={loadingBalances ? 'Loading balances, please wait…' : 'Print roster'}
                                className={`flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-lg transition shadow-sm ${
                                    loadingBalances ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                                }`}
                            >
                                {loadingBalances ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white flex-shrink-0" />
                                        Loading…
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                        </svg>
                                        Print
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>

                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                    {/* Stats row */}
                    {!loadingBowlers && (
                        <div className={`grid ${showFee ? 'grid-cols-3' : 'grid-cols-1'} gap-3 mb-5`}>
                            <div className="bg-white rounded-2xl shadow-sm p-4 text-center border border-gray-100">
                                <p className="text-3xl font-extrabold text-gray-900">{bowlers.length}</p>
                                <p className="text-xs text-gray-500 mt-0.5 font-medium">Total Bowlers</p>
                            </div>
                            {showFee && (
                                <div className="bg-white rounded-2xl shadow-sm p-4 text-center border border-green-100">
                                    <p className="text-3xl font-extrabold text-green-600">{paid}</p>
                                    <p className="text-xs text-green-600 mt-0.5 font-medium">Fee Paid</p>
                                </div>
                            )}
                            {showFee && (
                                <div className={`rounded-2xl shadow-sm p-4 text-center border ${unpaid > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
                                    <p className={`text-3xl font-extrabold ${unpaid > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                                        ${unpaid * WEBFEE_AMOUNT}
                                    </p>
                                    <p className={`text-xs mt-0.5 font-medium ${unpaid > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                                        {unpaid > 0 ? `${unpaid} Unpaid` : 'All Paid'}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Missing bowler request */}
                    {!loadingBowlers && (
                        <div className="flex justify-end mb-4">
                            <button
                                onClick={() => setClaimModalOpen(true)}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 active:bg-orange-200 border border-orange-200 px-4 py-2 rounded-xl transition shadow-sm"
                            >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                                Missing registered bowlers? Request here
                            </button>
                        </div>
                    )}

                    {/* Search */}
                    {!loadingBowlers && bowlers.length > 0 && (
                        <div className="mb-4">
                            <input
                                type="text"
                                placeholder="Search by name or TAT#…"
                                value={search}
                                onChange={e => handleSearchChange(e.target.value)}
                                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white shadow-sm"
                            />
                        </div>
                    )}

                    {loadingBowlers && (
                        <div className="flex justify-center py-16">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-500" />
                        </div>
                    )}
                    {errorBowlers && <p className="text-center text-red-500 py-10">{errorBowlers}</p>}
                    {!loadingBowlers && !errorBowlers && filteredBowlers.length === 0 && (
                        <p className="text-center text-gray-400 py-10">{search ? 'No bowlers match your search.' : 'No bowlers found.'}</p>
                    )}

                    {/* Bowler list */}
                    {!loadingBowlers && pagedBowlers.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <table className="w-full border-collapse">
                                <colgroup>
                                    <col style={{ width: '36px' }} />
                                    <col />
                                    <col style={{ width: '140px' }} />
                                    {showFee && <col style={{ width: '120px' }} />}
                                </colgroup>
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="px-4 py-2.5 text-left" />
                                        <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-400 uppercase tracking-widest">Bowler</th>
                                        <th className="px-4 py-2.5 text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest">Tournament Balance</th>
                                        {showFee && <th className="px-4 py-2.5 text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest">Platform Fee</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                {pagedBowlers.map((b, i) => {
                                    const globalIndex = (page - 1) * PAGE_SIZE + i;
                                    const bal = balances[b.clave];
                                    return (
                                        <tr
                                            key={b.clave}
                                            className={`${i !== pagedBowlers.length - 1 ? 'border-b border-gray-100' : ''} hover:bg-gray-50 transition-colors`}
                                        >
                                            {/* Row number */}
                                            <td className="px-4 py-3.5 text-right">
                                                <span className="text-xs font-bold text-gray-300">{globalIndex + 1}</span>
                                            </td>
                                            {/* Bowler name + TAT# */}
                                            <td className="px-4 py-3.5">
                                                <p className="font-semibold text-gray-900 text-sm leading-tight">{b.nombreCompleto}</p>
                                                <p className="text-xs text-gray-400 mt-0.5">TAT #{b.fichanal}</p>
                                            </td>
                                            {/* Tournament Balance */}
                                            <td className="px-4 py-3.5 text-center">
                                                {loadingBalances && !(b.clave in balances) ? (
                                                    <span className="text-xs text-gray-300">…</span>
                                                ) : bal === null || bal === undefined ? (
                                                    <span className="text-xs text-gray-400">—</span>
                                                ) : bal === 0 ? (
                                                    <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-green-100">
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                        Balanced
                                                    </span>
                                                ) : bal < 0 ? (
                                                    <span className="inline-block bg-red-50 text-red-600 text-xs font-semibold px-2.5 py-1 rounded-full border border-red-100">
                                                        Owes ${Math.abs(bal).toFixed(2)}
                                                    </span>
                                                ) : (
                                                    <span className="inline-block bg-blue-50 text-blue-600 text-xs font-semibold px-2.5 py-1 rounded-full border border-blue-100">
                                                        +${bal.toFixed(2)}
                                                    </span>
                                                )}
                                            </td>
                                            {/* Platform fee — only for 2026+ tournaments */}
                                            {showFee && (
                                                <td className="px-4 py-3.5 text-center">
                                                    {b.webfee === 1 ? (
                                                        <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-green-100">
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                            Paid
                                                        </span>
                                                    ) : (
                                                        <span className="inline-block bg-amber-50 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full border border-amber-200">
                                                            $10 Owed
                                                        </span>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loadingBowlers && totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4 px-1">
                            <p className="text-xs text-gray-400">
                                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredBowlers.length)} of {filteredBowlers.length}
                            </p>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition text-sm"
                                >
                                    ‹
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
                                    <button
                                        key={pg}
                                        onClick={() => setPage(pg)}
                                        className={`w-8 h-8 flex items-center justify-center rounded-lg border text-sm font-semibold transition ${
                                            pg === page
                                                ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                                                : 'border-gray-200 bg-white text-gray-600 hover:bg-indigo-50 hover:text-indigo-600'
                                        }`}
                                    >
                                        {pg}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed transition text-sm"
                                >
                                    ›
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Claim Modal ──────────────────────────────────────── */}
                {claimModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleCloseClaimModal} />
                        <div
                            className="relative bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl flex flex-col"
                            style={{ maxHeight: '92vh' }}
                        >
                            {/* Modal header */}
                            <div className="flex-shrink-0 bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-5 rounded-t-3xl sm:rounded-t-2xl">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="bg-white/20 rounded-xl p-2 flex-shrink-0">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                        </div>
                                        <div className="min-w-0">
                                            <h2 className="text-lg font-bold text-white">Missing Registered Bowlers?</h2>
                                            <p className="text-orange-100 text-xs truncate">{selectedTournament.torneoNombre}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCloseClaimModal}
                                        className="flex-shrink-0 text-white/80 hover:text-white hover:bg-white/20 rounded-lg p-1.5 transition ml-3"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Modal body */}
                            <div className="flex-1 overflow-y-auto px-6 py-5">
                                {claimSuccess ? (
                                    <div className="py-10 text-center">
                                        <div className="flex justify-center mb-4">
                                            <div className="bg-green-100 rounded-full p-4">
                                                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">Request Submitted!</h3>
                                        <p className="text-gray-600 text-sm mb-2">
                                            Your claim request has been submitted and will be processed as soon as possible.
                                        </p>
                                        <p className="text-gray-400 text-xs">This window will close automatically…</p>
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-sm text-gray-600 mb-4">
                                            Please search the name or TAT# of the bowler that is entered into{' '}
                                            <strong>{selectedTournament.torneoNombre}</strong>:
                                        </p>

                                        {/* Search input */}
                                        <div className="relative mb-4">
                                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                                            </svg>
                                            <input
                                                type="text"
                                                value={claimSearch}
                                                onChange={e => handleClaimSearch(e.target.value)}
                                                placeholder="Name or TAT#…"
                                                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-300 bg-white shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 transition"
                                            />
                                            {claimSearchLoading && (
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                    <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                                                </div>
                                            )}
                                        </div>

                                        {/* No results */}
                                        {claimSearchDone && !claimSearchLoading && claimResults.length === 0 && claimSearch.trim().length >= 2 && (
                                            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-gray-700">
                                                <p className="font-semibold text-amber-800 mb-1">No results found</p>
                                                <p>
                                                    Re-try your search or double check with the bowler that they are registered
                                                    into <strong>{selectedTournament.torneoNombre}</strong>.
                                                </p>
                                                <p className="mt-2 text-gray-500 text-xs">
                                                    If the issue persists please submit a support form from your dashboard for 24/7 assistance.
                                                </p>
                                            </div>
                                        )}

                                        {/* Search results */}
                                        {claimResults.length > 0 && (
                                            <div className="mb-4 rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
                                                {claimResults.map(b => {
                                                    const onRoster = bowlers.some(r => r.clave === b.clave);
                                                    const inCart = claimCart.some(c => c.clave === b.clave);
                                                    return (
                                                        <div key={b.clave} className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50">
                                                            <div className="min-w-0">
                                                                <p className="font-semibold text-sm text-gray-900 truncate">{b.nombreCompleto}</p>
                                                                <p className="text-xs text-gray-400">TAT# {b.fichanal} · {b.email}</p>
                                                            </div>
                                                            <button
                                                                onClick={() => !onRoster && handleClaim(b)}
                                                                disabled={onRoster || inCart}
                                                                className={`ml-3 flex-shrink-0 px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
                                                                    onRoster
                                                                        ? 'bg-indigo-100 text-indigo-600 cursor-default'
                                                                        : inCart
                                                                            ? 'bg-green-100 text-green-700 cursor-default'
                                                                            : 'bg-purple-500 hover:bg-purple-600 active:bg-purple-700 text-white'
                                                                }`}
                                                            >
                                                                {onRoster ? '✓ On Roster' : inCart ? '✓ Added' : 'Claim'}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Cart — bowlers queued for claim */}
                                        {claimCart.length > 0 && (
                                            <div className="mt-2">
                                                <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">
                                                    Bowlers to claim ({claimCart.length})
                                                </p>
                                                <div className="space-y-2">
                                                    {claimCart.map(b => (
                                                        <div
                                                            key={b.clave}
                                                            className="flex items-center justify-between bg-orange-50 border border-orange-100 rounded-xl px-4 py-2.5"
                                                        >
                                                            <div className="min-w-0">
                                                                <p className="font-semibold text-sm text-gray-900 truncate">{b.nombreCompleto}</p>
                                                                <p className="text-xs text-gray-400">TAT# {b.fichanal}</p>
                                                            </div>
                                                            <button
                                                                onClick={() => handleUnclaim(b.clave)}
                                                                className="ml-3 flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition"
                                                                aria-label="Remove"
                                                            >
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                                {claimError && (
                                                    <p className="mt-3 text-sm text-red-600 text-center">{claimError}</p>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Sticky submit */}
                            {!claimSuccess && claimCart.length > 0 && (
                                <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white rounded-b-3xl sm:rounded-b-2xl">
                                    <button
                                        onClick={handleClaimSubmit}
                                        disabled={claimSubmitting}
                                        className={`w-full py-3.5 px-4 rounded-xl font-bold text-white text-sm transition shadow-md ${
                                            claimSubmitting
                                                ? 'bg-red-300 cursor-not-allowed'
                                                : 'bg-purple-500 hover:bg-purple-600 active:scale-95'
                                        }`}
                                    >
                                        {claimSubmitting
                                            ? 'Submitting…'
                                            : `Submit request for: TAT# ${claimCart.map(b => b.fichanal).join(', ')}`
                                        }
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ── Tournament list view ──────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-100">
            {/* Header */}
            <div className="bg-white shadow-md">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-500 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100 transition"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <img src="/logo.avif" alt="TAT" className="h-10 w-auto" />
                    <div>
                        <h1 className="text-lg font-bold text-gray-900">My Roster</h1>
                        {recruiterName && <p className="text-xs text-gray-500 leading-tight">{recruiterName}</p>}
                    </div>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                {/* Recruiter tutorials */}
                <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="bg-red-100 rounded-lg p-1.5 flex-shrink-0">
                            <svg className="w-4 h-4 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                            </svg>
                        </div>
                        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Recruiter Tutorials</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <YoutubeLite id="f69wNyJcuIc" title="Claim missing bowlers english tutorial" />
                        <YoutubeLite id="DCBwyOPF2PY" title="Pide los bolichistas que te faltan tutorial" />
                    </div>
                </div>

                {/* Summary stats */}
                {!loadingTournaments && tournaments.length > 0 && (
                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100 flex items-center gap-4">
                            <div className="bg-indigo-100 rounded-xl p-3 flex-shrink-0">
                                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20H7m10 0v-2a3 3 0 00-5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-3xl font-extrabold text-gray-900">{totalBowlers}</p>
                                <p className="text-xs text-gray-500 font-medium">Bowlers Registered</p>
                            </div>
                        </div>
                        <div className={`rounded-2xl shadow-sm p-5 border flex items-center gap-4 ${totalOwed > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
                            <div className={`rounded-xl p-3 flex-shrink-0 ${totalOwed > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
                                <svg className={`w-6 h-6 ${totalOwed > 0 ? 'text-amber-600' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <p className={`text-3xl font-extrabold ${totalOwed > 0 ? 'text-amber-700' : 'text-green-600'}`}>${totalOwed}</p>
                                <p className={`text-xs font-medium ${totalOwed > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                    {totalOwed > 0 ? 'Platform Fees Owed' : 'All Fees Paid'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {loadingTournaments && (
                    <div className="flex justify-center py-16">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-500" />
                    </div>
                )}
                {errorTournaments && <p className="text-center text-red-500 py-10">{errorTournaments}</p>}
                {!loadingTournaments && !errorTournaments && tournaments.length === 0 && (
                    <div className="text-center py-16">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 inline-block">
                            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20H7m10 0v-2a3 3 0 00-5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <p className="text-base font-semibold text-gray-700">No bowlers registered yet</p>
                            <p className="text-sm text-gray-400 mt-1">Use "Register Your Bowlers" to get started.</p>
                        </div>
                    </div>
                )}

                {/* Tournament cards */}
                <div className="space-y-3">
                    {tournaments.map(t => {
                        const allPaid = t.unpaidCount === 0;
                        const feeApplicable = isFeeApplicable(t.fechaInicio);
                        return (
                            <button
                                key={t.torneoClave}
                                onClick={() => openTournament(t)}
                                className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all group"
                            >
                                <div className="flex items-center gap-4">
                                    {/* Status dot — amber only if fee applies and someone hasn't paid */}
                                    <div className={`w-1.5 self-stretch rounded-full flex-shrink-0 ${feeApplicable && !allPaid ? 'bg-amber-400' : 'bg-green-400'}`} />

                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-gray-900 text-sm leading-snug group-hover:text-indigo-700 transition-colors">{t.torneoNombre}</p>
                                        <p className="text-xs text-gray-400 mt-0.5">{formatDate(t.fechaInicio)}</p>
                                    </div>

                                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                        <span className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20H7m10 0v-2a3 3 0 00-5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            {t.bowlerCount}
                                        </span>
                                        {feeApplicable && (allPaid ? (
                                            <span className="bg-green-50 text-green-700 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-green-100">All paid</span>
                                        ) : (
                                            <span className="bg-amber-50 text-amber-700 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-amber-200">${t.unpaidCount * WEBFEE_AMOUNT} owed</span>
                                        ))}
                                    </div>

                                    <svg className="w-4 h-4 text-gray-300 group-hover:text-indigo-400 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

